import { defineStore } from 'pinia';
import { useErrorHandling } from 'src/core/composables/useErrorHandling';
import { useApiStore } from 'src/core/stores/api';
import { EnumType, FormatedEnum } from 'src/core/types/enum';
import { ResponseDataPreparationEnums } from 'src/core/types/enum/dataPreparation';

type DataPreparationEnumsStore = {
  enums: ResponseDataPreparationEnums;
  formatedEnums: FormatedEnum[];
};

/**
 * A store for managing data preparation enums data.
 */
export const useDataPreparationEnumsStore = defineStore(
  'dataPreparationEnums',
  {
    state: () =>
      ({
        enums: {} as ResponseDataPreparationEnums,
        formatedEnums: [] as FormatedEnum[],
      } as DataPreparationEnumsStore),

    getters: {
      /**
       * Get the formatted value of an enum.
       * @param enumVal - The enum value to format.
       * @returns The formatted enum value.
       */
      getFormatedEnumValue: (state) => (enumVal: string) => {
        return (
          state.formatedEnums.find(
            (enumValue: FormatedEnum) => enumValue[enumVal]
          )?.[enumVal] || enumVal
        );
      },

      /**
       * Get the enum instance based on the enum value.
       * @param enumVal - The enum value to search for.
       * @returns The enum value if found, undefined otherwise.
       */
      getEnumInstance: (state) => (enumVal: string) =>
        state.enums.statuses?.find((enumValue) => enumValue.value === enumVal),
    },

    actions: {
      /**
       * Get enums information.
       */
      async getEnums() {
        const apiStore = useApiStore();
        const { handleError } = useErrorHandling();
        try {
          const responseEnums =
            await apiStore.service.get<ResponseDataPreparationEnums>(
              '/data-preparation/enums'
            );
          // `countries` is a plain code list, not label/value pairs, so only
          // `statuses` is label-sorted and fed to `formatedEnums`.
          const statuses = [...responseEnums.data.statuses].sort((a, b) =>
            a.label.localeCompare(b.label)
          );
          this.enums = { statuses, countries: responseEnums.data.countries };
          this.formatedEnums = statuses.map((enumValue: EnumType) => ({
            [enumValue.value]: enumValue.label,
          }));
        } catch (e) {
          handleError('Enums error', e);
        }
      },
    },
  }
);
